import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLE_PRIORITY = { admin: 3, interviewer: 2, production: 1 };

function pickPrimary(roles) {
  if (!roles || roles.length === 0) return null;
  return [...roles].sort((a, b) => (ROLE_PRIORITY[b] || 0) - (ROLE_PRIORITY[a] || 0))[0];
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // The currently-active role for users with multiple roles. Persisted in localStorage.
  const [activeRole, setActiveRoleState] = useState(() => localStorage.getItem('active_role') || null);

  const normalizeUser = useCallback((data) => {
    if (!data) return null;
    const roles = data.roles && data.roles.length ? data.roles : (data.role ? [data.role] : []);
    return { ...data, roles, role: data.role || pickPrimary(roles) };
  }, []);

  const setActiveRole = (r) => {
    if (r) localStorage.setItem('active_role', r); else localStorage.removeItem('active_role');
    setActiveRoleState(r);
  };

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { setLoading(false); return; }
      const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      const u = normalizeUser(data);
      setUser(u);
      // If saved activeRole is invalid for this user, reset
      const saved = localStorage.getItem('active_role');
      if (!saved || !u.roles.includes(saved)) {
        const fallback = pickPrimary(u.roles);
        setActiveRole(fallback);
      }
    } catch {
      localStorage.removeItem('auth_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [normalizeUser]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (data.token) localStorage.setItem('auth_token', data.token);
    const u = normalizeUser(data);
    setUser(u);
    setActiveRole(pickPrimary(u.roles));
    return u;
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
    } catch {}
    localStorage.removeItem('auth_token');
    localStorage.removeItem('active_role');
    setUser(null);
    setActiveRoleState(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, activeRole, setActiveRole, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export { ROLE_PRIORITY };
